import { asManagerId, asPipelineId, asStatusId } from '../types/ids.ts'
import type { ManagerId, PipelineId, StatusId } from '../types/ids.ts'
import { rulePathsIn, validateRule } from './rules.ts'
import type { TriggerRule } from './rules.ts'
import { isWholeTemplate, templatePathsIn } from './template.ts'
import type { NodeIssue, NodeType } from './types.ts'

/**
 * What a node IS — the pure half of ADR-0007 §6's split contract. What a node
 * DOES lives in `services/engine/src/flows/nodes/`, keyed by the same closed
 * {@link NodeType}, so a spec without a handler is a compile error rather than
 * Directus's silent `status: 'unknown'`.
 *
 * Everything here is shape. Three checks a person would expect to find in
 * `validate` are deliberately **not** here, because they read the database and
 * this package may not:
 *
 * - a `targetStatusId` whose engine role is `awaiting_distribution` is refused
 *   (D6: entering that status *is* the distribution trigger, but distribution
 *   fires from the queue consumer and never from `facade.createLead`, so the
 *   card would sit there undistributed forever),
 * - an `owner` who does not participate in the target pipeline is refused,
 * - a `cycle: 'same'` node is refused at enable until the ludus-lead seam lands
 *   (ADR-0007 §5: the effect key's subject must be the sale).
 *
 * All three belong to the engine's save and enable paths and are named here so
 * that "the spec validated it" is never mistaken for "everything was checked".
 *
 * **Read "is refused" above as the contract, not as a report.** **No enable
 * check exists at all** — #165 owes it — so none of the three is refused at the
 * moment the wording implies. All three are caught at *run* time instead, by
 * three different mechanisms, and the difference between them is worth keeping:
 * the first is an explicit check written for the purpose
 * (`nodes/create_lead.ts`); the second is not a check at all but a consequence
 * of `prepareCreateLead`'s throw; the third is `sale_seam_missing`, raised in
 * `flows/effects.ts` when the subject cannot be named.
 */

/* ---------------------------------------------------------------- fields */

/**
 * What kind of control the console renders, and what kind of value the option
 * holds. Semantic rather than presentational — `status` rather than `select`,
 * because the console has to know it must offer *statuses* and which
 * pipeline's.
 */
export type NodeFieldType =
  | 'text'
  | 'number'
  | 'boolean'
  | 'tags'
  | 'select'
  | 'rule'
  | 'pipeline'
  | 'status'
  | 'manager'
  /**
   * One of the mails the instance's sink offers (#377).
   *
   * A distinct kind rather than `select` for the reason `status` is not one
   * either: the choices are not knowable here. They live behind an HTTP call to
   * whatever sends the mail, so the console must be told to *go and ask*, and a
   * `choices` array in this package would be the template names themselves —
   * exactly the vocabulary the adapter boundary exists to keep out.
   */
  | 'mail_template'

/**
 * One option, described well enough that the console's form is derived rather
 * than written per node type (study §9's `defineOperationApp.options`).
 *
 * There is no `label`. The words are the console's — Q3 ruled the Ukrainian
 * terminology on 2026-08-27 and it lives in the i18n files, in both `en` and
 * `uk`. `name` is the i18n key stem, so `core` declares the shape of a form and
 * never a syllable of it.
 */
export interface NodeField {
  name: string
  type: NodeFieldType
  required: boolean
  /**
   * Whether `{{ … }}` is legal here, **declared rather than assumed**
   * (ADR-0007 §6). Templates belong on value fields (`name`, `price`, `tags`);
   * identity fields (`targetPipelineId`, `targetStatusId`, `owner`) are
   * concrete ids validated at save and again at run, so a template in one is a
   * validation issue rather than a runtime surprise.
   */
  templatable: boolean
  /** The closed choice list, for `select`. */
  choices?: readonly string[]
  /**
   * The value an absent option takes — applied by {@link NodeSpec.parse} and
   * prefilled by the console, from this one place.
   *
   * It is therefore part of the contract rather than a convenience: changing a
   * default changes what an already-saved flow that omitted the option does.
   * No field is both `required` and defaulted; the two would contradict each
   * other, and `nodes.test.ts` pins that.
   */
  default?: unknown
  /**
   * Which option holds the pipeline id whose statuses or managers this field
   * must offer. Absent means the flow's own pipeline. Without it the console
   * would have to know that `targetStatusId` belongs to `targetPipelineId`,
   * which is exactly the per-type knowledge the spec exists to carry.
   */
  scopedBy?: string
}

/**
 * One row of the collapsed card body on the canvas (Directus's `overview`).
 *
 * `label` is the **option's name**, which is also its i18n key stem and its
 * entry in {@link NodeSpec.fields} — so the console translates the row and
 * resolves an id to a human name without learning what any node type's options
 * mean.
 */
export interface OverviewRow {
  label: string
  text: string
}

/* ------------------------------------------------------------------ spec */

export interface NodeSpec<O> {
  readonly type: NodeType
  readonly fields: readonly NodeField[]
  /** Shape only — see this module's header for the three checks the engine owes. */
  validate(options: unknown): NodeIssue[]
  /**
   * The narrowing seam. `null` exactly when {@link validate} has something to
   * say, so the runtime never asserts that a JSON blob is what it claims.
   *
   * This is what the generic parameter buys, and it is why `NodeSpec` is not
   * `NodeSpec<any>` as ADR-0007 §6 sketched it: a registry typed `any` is a
   * hole in the one place the graph stops being trusted, and a parameter that
   * only appeared in `overview` would have forced the console — whose options
   * come straight out of the blob — to cast at every call.
   */
  parse(options: unknown): O | null
  overview(options: unknown): readonly OverviewRow[]
}

/* --------------------------------------------------------- option shapes */

export type StatusEnteredOptions = {
  statusId: StatusId
  /** `null` = from any status. */
  fromStatusId: StatusId | null
}

export type ConditionOptions = {
  rule: TriggerRule
}

/** Whether the created card joins the source card's cycle or opens a new one. */
export type FlowCycle = 'same' | 'new'
export const FLOW_CYCLES: readonly FlowCycle[] = ['same', 'new'] as const

/** A templatable value: a literal, or a string carrying `{{ … }}`. */
export type Templatable<T> = T | string

export type CreateLeadOptions = {
  targetPipelineId: PipelineId
  targetStatusId: StatusId
  owner: ManagerId
  name: string
  price: Templatable<number> | null
  tags: readonly string[]
  copyContact: boolean
  copyFunnel: boolean
  cycle: FlowCycle
}

export type UpdateLeadOptions = {
  targetPipelineId: PipelineId
  targetStatusId: StatusId | null
  owner: ManagerId | null
  name: string | null
  price: Templatable<number> | null
  tags: readonly string[] | null
}

/**
 * One transactional mail, named by the sink's own word for it (#377).
 *
 * Every field is a *string or nothing* on purpose. The mail's shape, its copy
 * and its locales belong to whatever sends it; what a flow decides is which
 * mail, about which card, and — when the card's own contact is the wrong
 * answer — to whom.
 */
export type SendEmailOptions = {
  /** The sink's template name. Opaque here: core never learns what it renders. */
  template: string
  /** An explicit recipient, or `null` to use the card's contact. */
  to: string | null
  /** A forced locale, or `null` to let the sink choose from its own record. */
  lang: string | null
}

/* --------------------------------------------------- the generic checker */

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const hasTemplate = (value: unknown): boolean => templatePathsIn(value).length > 0

/**
 * `field` is omitted, never `''`, for an issue about the node itself — that is
 * what {@link NodeIssue} declares, and the console looks a field up by this
 * name. Two spellings of "no field" would make one of them a lookup miss.
 */
const issue = (code: NodeIssue['code'], field: string | undefined, message: string): NodeIssue =>
  field === undefined || field === '' ? { code, message } : { code, field, message }

/**
 * Check one options object against its spec's declared fields.
 *
 * Every spec runs this first, so "unknown option", "required", the type table
 * and the templatable rule are written once. A spec adds only the checks its
 * own fields cannot express.
 *
 * An **unknown option is an issue**, not something ignored. The console and the
 * engine are built from one source, so tolerance would buy nothing and cost
 * silence: a retired option, or one whose name was mistyped in a raw-JSON
 * field, would save clean and then do nothing — the same trade `bodyObject`
 * makes on the engine's request bodies.
 */
function validateFields(options: unknown, fields: readonly NodeField[]): NodeIssue[] {
  if (!isPlainObject(options)) {
    return [issue('not_an_object', undefined, 'options must be an object')]
  }

  const issues: NodeIssue[] = []
  const declared = new Set(fields.map((f) => f.name))
  for (const key of Object.keys(options)) {
    if (!declared.has(key)) issues.push(issue('unknown_option', key, `unknown option ${key}`))
  }

  for (const field of fields) {
    const value = options[field.name]
    const absent = value === undefined || value === null

    if (absent) {
      if (field.required) issues.push(issue('required', field.name, `${field.name} is required`))
      continue
    }

    if (!field.templatable && hasTemplate(value)) {
      issues.push(
        issue('not_templatable', field.name, `${field.name} is an id, not a template target`),
      )
      continue
    }

    issues.push(...validateFieldValue(field, value))
  }

  return issues
}

function validateFieldValue(field: NodeField, value: unknown): NodeIssue[] {
  const { name, type } = field

  switch (type) {
    // `mail_template` shares this arm deliberately: a non-empty string, and
    // nothing more. Whether the sink *has* a mail by that name is a question
    // only the sink can answer, so it is answered where a wrong answer costs a
    // rejected send rather than an unsaveable graph — the same division
    // `targetStatusId` follows, whose existence is likewise checked at run.
    case 'text':
    case 'pipeline':
    case 'status':
    case 'manager':
    case 'mail_template':
      if (typeof value !== 'string')
        return [issue('not_a_string', name, `${name} must be a string`)]
      if (value.trim() === '') return [issue('empty', name, `${name} must not be empty`)]
      return []

    case 'select':
      if (typeof value !== 'string')
        return [issue('not_a_string', name, `${name} must be a string`)]
      return field.choices?.includes(value)
        ? []
        : [issue('invalid_enum', name, `${name} must be one of ${field.choices?.join(', ')}`)]

    case 'number':
      if (typeof value === 'number' && Number.isFinite(value)) return []
      // A templatable number may arrive as a string, but only as **one whole**
      // `{{ … }}`, because that is the grammar that keeps the value's type.
      // `"1200"` is a literal in the wrong type, and `"{{ $lead.price }} USD"`
      // renders to a string — both would put a non-number in a number field.
      if (field.templatable && typeof value === 'string' && isWholeTemplate(value)) return []
      return [issue('not_a_number', name, `${name} must be a number`)]

    case 'boolean':
      if (typeof value !== 'boolean') {
        return [issue('not_a_boolean', name, `${name} must be true or false`)]
      }
      return []

    case 'tags':
      if (!Array.isArray(value)) return [issue('not_an_array', name, `${name} must be an array`)]
      return value.every((t) => typeof t === 'string')
        ? []
        : [issue('not_a_string', name, `${name} must hold strings`)]

    case 'rule':
      if (!isPlainObject(value)) return [issue('not_an_object', name, `${name} must be an object`)]
      return validateRule(value).map((i) => ({
        ...i,
        field: i.field === undefined ? name : `${name}.${i.field}`,
      }))
  }
}

/**
 * Read one option, falling back to its field's declared default. The single
 * place a default is applied, so `fields` stays the only statement of what an
 * absent option means.
 */
/**
 * A field's declared default, **copied if it is mutable**.
 *
 * `create_lead`'s `tags` declares `default: []`, and that array object lives in
 * a module-level `NodeField`. Handed out by reference, one `push` in any caller
 * would change what every later read in that isolate treats as the default — a
 * flow behaving differently on its second run with nothing in the diff to
 * explain it, which is the determinism failure `packages/core/CLAUDE.md`'s "Why
 * pure" exists to prevent.
 *
 * It lives here, in one function both public readers go through, rather than as
 * a copy at each call site: a third reader added later inherits the fix instead
 * of having to remember it.
 */
function defaultValueOf(field: NodeField | undefined): unknown {
  const value = field?.default ?? null
  return Array.isArray(value) ? [...value] : value
}

/**
 * An option's value, or its declared default when the option is **absent**.
 *
 * **Absent, not empty** — and the distinction is load-bearing at run time. The
 * runtime fills defaults *before* rendering (`walk.ts`: `renderOptions(
 * withNodeDefaults(...))`), so by the time a handler parses rendered options
 * every defaulted key is present. A whole-template that does not resolve
 * renders to `null` (`renderString`), and treating that `null` as absent
 * re-applied the default **after** rendering — putting the literal text
 * `{{ $lead.name }}` on the created card, which is precisely the outcome
 * `withNodeDefaults` exists to prevent. A key that is present and `null` has
 * been answered: the answer is "nothing".
 *
 * `undefined` still defaults, which is what the non-runtime readers want —
 * `overview` and the console both call this on raw stored options that never
 * went through `withNodeDefaults`.
 */
function defaulted(
  options: Record<string, unknown>,
  fields: readonly NodeField[],
  name: string,
): unknown {
  if (name in options && options[name] !== undefined) return options[name]
  return defaultValueOf(fields.find((f) => f.name === name))
}

const text = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return value.map(text).join(', ')
  if (typeof value === 'object') return JSON.stringify(value) ?? ''
  return String(value)
}

/* ------------------------------------------------- trigger.status_entered */

const STATUS_ENTERED_FIELDS: readonly NodeField[] = [
  { name: 'statusId', type: 'status', required: true, templatable: false },
  { name: 'fromStatusId', type: 'status', required: false, templatable: false, default: null },
]

/**
 * "When a lead in this pipeline **enters** [status] (from [any | status])" —
 * the v1 trigger sentence, and not "moves to" (ADR-0007 §2). What that costs
 * and buys is in {@link triggersOf}, which is where the sentence becomes rows.
 */
const statusEnteredSpec: NodeSpec<StatusEnteredOptions> = {
  type: 'trigger.status_entered',
  fields: STATUS_ENTERED_FIELDS,
  validate: (options) => validateFields(options, STATUS_ENTERED_FIELDS),
  parse: (options) => {
    if (!isPlainObject(options) || statusEnteredSpec.validate(options).length > 0) return null
    const from = options.fromStatusId
    return {
      statusId: asStatusId(options.statusId as string),
      fromStatusId: typeof from === 'string' ? asStatusId(from) : null,
    }
  },
  overview: (options) => {
    if (!isPlainObject(options)) return []
    const rows: OverviewRow[] = [{ label: 'statusId', text: text(options.statusId) }]
    if (typeof options.fromStatusId === 'string') {
      rows.push({ label: 'fromStatusId', text: options.fromStatusId })
    }
    return rows
  },
}

/* ---------------------------------------------------------------- condition */

const CONDITION_FIELDS: readonly NodeField[] = [
  { name: 'rule', type: 'rule', required: true, templatable: false },
]

/**
 * `true` follows `resolve`, `false` follows `reject`. A rule the evaluator
 * cannot apply traverses no edge at all — see `RuleVerdict`.
 *
 * `rule` is not templatable, and the reason is not symmetry: a rule whose
 * *shape* came from the chain could not be validated at save, which is where
 * every guarantee in this folder is bought.
 */
const conditionSpec: NodeSpec<ConditionOptions> = {
  type: 'condition',
  fields: CONDITION_FIELDS,
  validate: (options) => {
    const issues = validateFields(options, CONDITION_FIELDS)
    if (issues.length > 0 || !isPlainObject(options)) return issues
    // `{}`, `{ _and: [] }` and `{ _or: [] }` are grammatical and name no field:
    // one always resolves, the other always rejects. Either is a branch that
    // draws on the canvas and decides nothing, so it is caught at save rather
    // than discovered in a run log.
    if (rulePathsIn(options.rule).length === 0) {
      issues.push(issue('empty', 'rule', 'a condition needs something to test'))
    }
    return issues
  },
  parse: (options) => {
    if (!isPlainObject(options) || conditionSpec.validate(options).length > 0) return null
    return { rule: options.rule as TriggerRule }
  },
  overview: (options) => {
    if (!isPlainObject(options)) return []
    return [{ label: 'rule', text: text(options.rule) }]
  },
}

/* -------------------------------------------------------------- create_lead */

const CREATE_LEAD_FIELDS: readonly NodeField[] = [
  { name: 'targetPipelineId', type: 'pipeline', required: true, templatable: false },
  {
    name: 'targetStatusId',
    type: 'status',
    required: true,
    templatable: false,
    scopedBy: 'targetPipelineId',
  },
  {
    name: 'owner',
    type: 'manager',
    required: true,
    templatable: false,
    scopedBy: 'targetPipelineId',
  },
  { name: 'name', type: 'text', required: false, templatable: true, default: '{{ $lead.name }}' },
  { name: 'price', type: 'number', required: false, templatable: true, default: null },
  { name: 'tags', type: 'tags', required: false, templatable: true, default: [] },
  { name: 'copyContact', type: 'boolean', required: false, templatable: false, default: true },
  { name: 'copyFunnel', type: 'boolean', required: false, templatable: false, default: true },
  {
    name: 'cycle',
    type: 'select',
    required: false,
    templatable: false,
    choices: FLOW_CYCLES,
    default: 'same',
  },
]

/**
 * The action the worked example turns on: an order entering «paid» yields one
 * Delivery card (D6).
 *
 * `owner` is required because the node **always names a manager** (ruled
 * 2026-08-27): no unowned card is ever created by a flow. "Same owner as the
 * source card" is a picker shortcut that resolves to a concrete manager at save
 * time, not a value this option can hold.
 *
 * `tags` is a list whose *entries* may be templates; the whole list is not one
 * template. `["{{ $lead.funnel }}"]` is the shape that copies a value in, and
 * it keeps the option's type an array at rest as well as after rendering.
 */
const createLeadSpec: NodeSpec<CreateLeadOptions> = {
  type: 'create_lead',
  fields: CREATE_LEAD_FIELDS,
  validate: (options) => validateFields(options, CREATE_LEAD_FIELDS),
  parse: (options) => {
    if (!isPlainObject(options) || createLeadSpec.validate(options).length > 0) return null
    const read = (name: string) => defaulted(options, CREATE_LEAD_FIELDS, name)
    const price = read('price')
    const tags = read('tags')
    return {
      targetPipelineId: asPipelineId(options.targetPipelineId as string),
      targetStatusId: asStatusId(options.targetStatusId as string),
      owner: asManagerId(options.owner as string),
      // `read` may now answer `null` — a name template that resolved to
      // nothing. An empty name is the honest result; `String(null)` would name
      // the card "null", which reads as a bug rather than as an empty field.
      name: typeof read('name') === 'string' ? (read('name') as string) : '',
      price: typeof price === 'number' || typeof price === 'string' ? price : null,
      tags: Array.isArray(tags) ? (tags as readonly string[]) : [],
      copyContact: read('copyContact') === true,
      copyFunnel: read('copyFunnel') === true,
      cycle: read('cycle') === 'new' ? 'new' : 'same',
    }
  },
  overview: (options) => {
    if (!isPlainObject(options)) return []
    const rows: OverviewRow[] = [
      { label: 'targetPipelineId', text: text(options.targetPipelineId) },
      { label: 'targetStatusId', text: text(options.targetStatusId) },
      { label: 'owner', text: text(options.owner) },
      { label: 'name', text: text(defaulted(options, CREATE_LEAD_FIELDS, 'name')) },
      { label: 'cycle', text: text(defaulted(options, CREATE_LEAD_FIELDS, 'cycle')) },
    ]
    if (options.price !== undefined && options.price !== null) {
      rows.push({ label: 'price', text: text(options.price) })
    }
    if (Array.isArray(options.tags) && options.tags.length > 0) {
      rows.push({ label: 'tags', text: text(options.tags) })
    }
    return rows
  },
}

/* -------------------------------------------------------------- update_lead */

const UPDATE_LEAD_FIELDS: readonly NodeField[] = [
  { name: 'targetPipelineId', type: 'pipeline', required: true, templatable: false },
  {
    name: 'targetStatusId',
    type: 'status',
    required: false,
    templatable: false,
    default: null,
    scopedBy: 'targetPipelineId',
  },
  {
    name: 'owner',
    type: 'manager',
    required: false,
    templatable: false,
    default: null,
    scopedBy: 'targetPipelineId',
  },
  { name: 'name', type: 'text', required: false, templatable: true, default: null },
  { name: 'price', type: 'number', required: false, templatable: true, default: null },
  { name: 'tags', type: 'tags', required: false, templatable: true, default: null },
]

/** The fields that make an `update_lead` node do something. */
const UPDATE_LEAD_MUTABLE: readonly string[] = ['targetStatusId', 'owner', 'name', 'price', 'tags']

/**
 * Q12's node, ruled into v1 on 2026-08-27: when an order moves to «cancelled»
 * the flow must close or archive the Delivery card it created, so create-only
 * does not close the loop the worked example opens.
 *
 * **The handler ships with #256**; what follows describes what it does, not
 * what it owes. It was a refusal stub until then — registered, because
 * `NODE_HANDLERS` is closed in both directions and a spec without a handler
 * would not compile, but failing every run — because the card it must find is
 * reachable only through the ludus-lead seam, and nothing consumed that seam
 * until #253 filled `$sale`.
 *
 * **It writes locally, and only locally** (ADR-0007 §6). It goes through the
 * target pipeline's own update; it never calls `writeStatus` and never reaches
 * an adapter. That is not tidiness: §8's recorded hole is that causation resets
 * at an external boundary — `writeStatus` → the CRM → a webhook →
 * `lead.updated` at depth 0 — and the echo guard that closes it is owed when
 * the first write-back node ships. A v1 node that could push to a source would
 * open that hole in the same release that defers its guard.
 *
 * The card it updates is not an option: it is the sibling of the source card on
 * the same sale in `targetPipelineId`, which is the same subject the effect key
 * reserves (ADR-0007 §5). There is deliberately no `leadId` field — a flow that
 * could name an arbitrary card would need a permission model, and v1 has one
 * bearer token per tenant.
 *
 * `nothing_to_do` is a real issue rather than a tolerated no-op: a node with a
 * target and no change would draw on the canvas, run, log a `done` step, and
 * mean nothing.
 */
const updateLeadSpec: NodeSpec<UpdateLeadOptions> = {
  type: 'update_lead',
  fields: UPDATE_LEAD_FIELDS,
  validate: (options) => {
    const issues = validateFields(options, UPDATE_LEAD_FIELDS)
    if (!isPlainObject(options)) return issues
    const touches = UPDATE_LEAD_MUTABLE.some(
      (name) => options[name] !== undefined && options[name] !== null,
    )
    if (!touches) {
      issues.push(issue('nothing_to_do', undefined, 'name at least one field to change'))
    }
    return issues
  },
  parse: (options) => {
    if (!isPlainObject(options) || updateLeadSpec.validate(options).length > 0) return null
    const str = (v: unknown) => (typeof v === 'string' ? v : null)
    const status = str(options.targetStatusId)
    const owner = str(options.owner)
    const price = options.price
    return {
      targetPipelineId: asPipelineId(options.targetPipelineId as string),
      targetStatusId: status === null ? null : asStatusId(status),
      owner: owner === null ? null : asManagerId(owner),
      name: str(options.name),
      price: typeof price === 'number' || typeof price === 'string' ? price : null,
      tags: Array.isArray(options.tags) ? (options.tags as readonly string[]) : null,
    }
  },
  overview: (options) => {
    if (!isPlainObject(options)) return []
    const rows: OverviewRow[] = [
      { label: 'targetPipelineId', text: text(options.targetPipelineId) },
    ]
    for (const name of UPDATE_LEAD_MUTABLE) {
      const value = options[name]
      if (value !== undefined && value !== null) rows.push({ label: name, text: text(value) })
    }
    return rows
  },
}

/* --------------------------------------------------------------- send_email */

const SEND_EMAIL_FIELDS: readonly NodeField[] = [
  { name: 'template', type: 'mail_template', required: true, templatable: false },
  { name: 'to', type: 'text', required: false, templatable: true, default: null },
  { name: 'lang', type: 'text', required: false, templatable: true, default: null },
]

/**
 * `send_email` — schedule one transactional mail about this card (#377).
 *
 * **`template` is an opaque name and stays one.** It is the sink's word for a
 * mail, not ours: this package must be able to describe the node without
 * knowing that a tecraft deployment spells one of them `order-confirmation`.
 * So the field is free text validated for shape alone, the choices the console
 * offers come from the sink at runtime, and `NodeFieldType` gains no `template`
 * kind — a closed `choices` list here would be exactly the leak the adapter
 * boundary exists to prevent.
 *
 * **`to` defaults to nothing, and that is not the same as defaulting to
 * empty.** A recipient is normally the card's own contact, which lives in a row
 * this package may not read, so resolving it is the handler's job and an absent
 * option means «use the card's contact». A literal or a template here is the
 * override for the case where it must go somewhere else, and it is checked at
 * run time like any other rendered value.
 *
 * **`lang` absent means the sink decides.** tecraft picks from `customers.locale`
 * with an English fallback, and a null we pass through is honest about not
 * knowing; a `'en'` written here would silently overrule a customer who had
 * asked for Czech.
 */
const sendEmailSpec: NodeSpec<SendEmailOptions> = {
  type: 'send_email',
  fields: SEND_EMAIL_FIELDS,
  validate: (options) => {
    const issues = validateFields(options, SEND_EMAIL_FIELDS)
    if (!isPlainObject(options)) return issues
    // `required` catches an absent template; this catches the one a console
    // sends as an empty string, which is a field a person left blank rather
    // than a field they never saw.
    const template = options.template
    if (typeof template === 'string' && template.trim().length === 0) {
      issues.push(issue('empty', 'template', 'name the mail this node sends'))
    }
    return issues
  },
  parse: (options) => {
    if (!isPlainObject(options) || sendEmailSpec.validate(options).length > 0) return null
    const read = (name: string) => defaulted(options, SEND_EMAIL_FIELDS, name)
    const str = (value: unknown) => {
      if (typeof value !== 'string') return null
      const trimmed = value.trim()
      return trimmed.length === 0 ? null : trimmed
    }
    return {
      template: (options.template as string).trim(),
      to: str(read('to')),
      lang: str(read('lang')),
    }
  },
  overview: (options) => {
    if (!isPlainObject(options)) return []
    const rows: OverviewRow[] = [{ label: 'template', text: text(options.template) }]
    for (const name of ['to', 'lang'] as const) {
      const value = options[name]
      if (value !== undefined && value !== null && value !== '') {
        rows.push({ label: name, text: text(value) })
      }
    }
    return rows
  },
}

/* -------------------------------------------------------------- the registry */

/**
 * The options each node type parses to. Written as a mapped type over
 * {@link NodeType} in {@link NODE_SPECS}, so a fifth node type is a compile
 * error here until it has a spec — the same closed-both-ways guarantee
 * `EVENT_TYPE_COVERAGE` gives the event vocabulary.
 */
export type NodeOptionsByType = {
  'trigger.status_entered': StatusEnteredOptions
  condition: ConditionOptions
  create_lead: CreateLeadOptions
  update_lead: UpdateLeadOptions
  send_email: SendEmailOptions
}

export type NodeSpecRegistry = { readonly [T in NodeType]: NodeSpec<NodeOptionsByType[T]> }

export const NODE_SPECS: NodeSpecRegistry = {
  'trigger.status_entered': statusEnteredSpec,
  condition: conditionSpec,
  create_lead: createLeadSpec,
  update_lead: updateLeadSpec,
  send_email: sendEmailSpec,
}

/**
 * One node's options with its spec's declared defaults filled in — **before**
 * anything renders them.
 *
 * {@link NodeSpec.parse} applies the same defaults, and that is one step too
 * late for the runtime: the walk renders `{{ … }}` against the chain and only
 * then hands the options to a handler that parses them. A `create_lead` node
 * that simply omits `name` would therefore have its default applied *after*
 * rendering and would title the card with the literal seven characters
 * `{{ $lead.name }}`.
 *
 * So the order is defaults → render → parse, and this is the first step of it.
 * It reads `fields` rather than reimplementing the rule, because "what an
 * absent option means" is declared in exactly one place and a second copy is
 * how the console and the engine start disagreeing about a saved flow.
 *
 * `null` counts as absent, as it does in `parse`: a console that clears a field
 * writes `null`, and two spellings of "no value" would make one of them behave
 * differently from the other.
 */
export function withNodeDefaults(
  type: NodeType,
  options: Record<string, unknown>,
): Record<string, unknown> {
  const filled: Record<string, unknown> = { ...options }
  for (const field of NODE_SPECS[type].fields) {
    if (field.default === undefined) continue
    const value = filled[field.name]
    if (value === undefined || value === null) filled[field.name] = defaultValueOf(field)
  }
  return filled
}

/**
 * A node's role is read from {@link nodeRole}, never carried a second time on
 * its spec. Two copies of the same fact would let a graph be validated as one
 * shape and executed as another, and nothing here needs the copy: the console
 * groups its palette by `nodeRole(spec.type)`.
 */
